'use client'
import { useEffect, useState } from 'react'
import Button from '../button'
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import { useAddProducerProductsMutation, useGetIngredientsQuery } from 'graphql/generated/graphql'
import { Spinner } from 'flowbite-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import SubmitButton from '../ui/Button'
import { toast } from 'react-toastify'
import AddManually from './components/add-manually'
import type { TierLimits } from '@/lib/tier-limits'


export const AddProduct = ({
  listingId,
  setOpen,
  tierLimits,
  currentProductCount
} : {
  listingId: number
  setOpen: Function
  tierLimits?: TierLimits
  currentProductCount?: number
}) => {
  // Provide default values if not passed
  const limits = tierLimits || {
    products: Infinity,
    canAddProducts: true,
    canAddManually: true,
  }
  const productCount = currentProductCount || 0
  const [query, setQuery] = useState('')
  const [productListVisible, setProductListVisible] = useState(false)
  const [addProducerProducts, {loading: addProductLoading}] = useAddProducerProductsMutation()

  const [filteredItems, setFilteredItems] = useState<any>([])
  const [formData, setFormData] = useState<any>({
    ingredients: [],
  })

  const {
    data: ingredients,
    error,
    loading,
  } = useGetIngredientsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      limit: 1000,
      offset: 0,
      searchTerm: query.trim() || '',
    }
  })

  useEffect(() => {
    if(ingredients?.getIngredients){
      setFilteredItems(ingredients.getIngredients)
    }
  }, [ingredients])

  const handleSearch = (e: any) => {
    const value = e.target.value
    setQuery(value)

    // Start with all ingredients
    let filtered = ingredients?.getIngredients || []

    // Apply search filter - show all matching ingredients (locked state handled in UI)
    if (value.trim() !== '') {
      filtered = filtered.filter((ingredient) =>
        ingredient?.name?.toLowerCase().includes(value.toLowerCase())
      )
    }

    setFilteredItems(filtered)
  }

  const checkIsSelected = (item: any) => {
    return formData?.ingredients?.some((selected) => selected?.name === item?.name)
  }

    const handleAddRemove = (product: any) => {
    if (formData?.ingredients.some((item) => item?.name === product?.name)) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        ingredients: prevFormData.ingredients.filter(
          (p: any) => p?.name !== product.name
        ),
      }))
    } else {
      const { __typename, ingredients, ...rest } = product;
      setFormData((prevFormData) => ({
        ...prevFormData,
        ingredients: [...prevFormData.ingredients, product]
      }))
    }
  }

  const handleSubmit = async (e: any) => {
        e.preventDefault()
        try {
            if (!listingId || isNaN(Number(listingId))) {
                throw new Error('Invalid listing ID');
            }
            
            if (!formData.ingredients || formData.ingredients.length === 0) {
                toast.warning('Please add at least one product before submitting');
                return;
            }

            // Validate product limit
            const totalProducts = productCount + formData.ingredients.length
            if (totalProducts > limits.products) {
                const remaining = limits.products - productCount
                toast.error(`Free producer accounts are limited to ${limits.products} products, consider upgrading to add more.`)
                return
            }

            const processedIngredients = formData.ingredients.map((ingredient: any) => {
                const {
                    name,
                    ingredient_id,
                    type,
                    file,
                    description,
                    illustrationImage,
                    mainImage,
                    isSeasonal,
                    pairing,
                    cooking,
                    cookingGuidance,
                    choosing,
                    flavoring,
                    preparation,
                    methods,
                    allow_usage
                } = ingredient;
                
                return {
                    name,
                    ...(ingredient_id && { ingredient_id: Number(ingredient_id) }),
                    ...(type && { type }),
                    ...(description && { description }),
                    ...(illustrationImage && { illustrationImage }),
                    ...(mainImage && { mainImage }),
                    ...(typeof isSeasonal === 'boolean' && { isSeasonal }),
                    ...(pairing && { pairing }),
                    ...(cooking && { cooking }),
                    ...(cookingGuidance && { cookingGuidance }),
                    ...(choosing && { choosing }),
                    ...(flavoring && { flavoring }),
                    ...(preparation && { preparation }),
                    ...(methods && { methods }),
                    ...(file && { file }),
                    ...(typeof allow_usage === 'boolean' && { allow_usage })
                };
            });
            
            
            const res = await addProducerProducts({
                variables: {
                    listingID: listingId,
                    input: processedIngredients,
                },
            })
            setFormData({ ingredients: [] })
            if(res.data?.addProducerProducts) {
                setOpen(false)
                setFormData((prevFormData: any) => ({
                    ...prevFormData,
                    ingredients: [],
                }))
                toast.success('Products added successfully')
            }
        } catch (error) {
            console.error('Error adding products:', error?.message)
            toast.error(error?.message)
        }
    }

  return (
    <>
      <div
        className={`flex flex-col gap-4 rounded-lg bg-[#F7F7F7] md:hidden ${
          !productListVisible && 'xs:pb-0'
        } p-4  `}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Added Products</h3>
          <div className="flex items-center gap-2 font-semibold">
            <span>{formData?.ingredients?.length}</span>
            {productListVisible ? (
              <ChevronUp
                size={24}
                onClick={() => setProductListVisible(false)}
              />
            ) : (
              <ChevronDown
                size={24}
                onClick={() => setProductListVisible(true)}
              />
            )}
          </div>
        </div>
        <div
          className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${
            productListVisible ? 'max-h-[1000px]' : 'max-h-0'
          }`}
        >
          <div className="flex flex-1 flex-col gap-3">
            {formData.ingredients?.map((product, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between rounded-lg border border-[#CCE9AD] bg-white p-2 shadow-sm"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <img
                    src={
                      product?.illustrationImage ?? '/assets/icons/Broccoli.svg'
                    }
                    alt="product"
                    className="h-[61px] w-[61px] rounded-full object-cover"
                  />
                  <h1 className="font-bold">{product?.name}</h1>
                </div>
                <button
                  type="button"
                  aria-label="Remove product"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[5px] bg-[#F7F7F7] text-[#202020] hover:text-red-500"
                  onClick={() => handleAddRemove(product)}
                >
                  <X className="h-5 w-5" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid h-full grid-cols-1 gap-8 overflow-x-hidden px-2 pb-24 sm:px-5 md:min-w-[90%] md:pb-0 xl:min-w-[800px] xl:grid-cols-12">
        <div className="flex flex-col gap-4 xl:col-span-7">
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="relative flex-1 min-w-0">
              <input
                name="query"
                placeholder="Search Products"
                type="search"
                value={query}
                onChange={handleSearch}
                className={`h-12 w-full rounded-full border border-[#CBD7DD] py-1 px-6 pl-12 pr-3 text-base text-off-black placeholder-[#6B6B6B] transition-all duration-100 ease-in-out focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary`}
              />
              <img
                src="/images/search.svg"
                alt="search"
                className="absolute left-4 top-1/2 z-50 h-6 w-6 -translate-y-1/2 transform focus-within:hidden"
              />
            </div>
            <AddManually
              setFormData={setFormData}
              type='product'
              plusButton={true}
              canAddManually={limits.canAddManually}
            />
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-[#202020]">{`${filteredItems?.length} results found`}</span>
            <span className="text-sm text-[#6B6B6B]">{`Search: ${query}`}</span>
          </div>

          {/* Product limit counter */}
          {limits.products !== Infinity && (
            <div className="flex items-center gap-2 py-2 px-4 bg-gray-50 rounded-md border border-gray-200">
              <span className="text-sm text-gray-600">
                Adding {formData.ingredients.length} product{formData.ingredients.length === 1 ? '' : 's'}
              </span>
              <span className={`text-sm font-medium ${
                (productCount + formData.ingredients.length) > limits.products
                  ? 'text-red-600'
                  : 'text-green-600'
              }`}>
                ({productCount + formData.ingredients.length}/{limits.products} used)
              </span>
            </div>
          )}

          {filteredItems?.length > 0 && !loading ? (
            <div className="grid grid-cols-2 gap-[20px] overflow-y-auto md:h-[633px] md:grid-cols-2 xxs:grid-cols-1">
              {filteredItems.map((product: any, idx: number) => {
                const isSelected = checkIsSelected(product)

                return (
                <div
                  key={idx}
                  className={`relative flex min-h-[260px] h-[260px] flex-col items-center justify-between gap-3 rounded-lg border border-[#E6E6E6] ${
                    isSelected ? 'bg-[#F3FAEB]' : 'bg-white'
                  } py-5 shadow-sm`}
                >
                  <div className="flex flex-col items-center gap-5 text-center">
                    <img
                      src={
                        product?.illustrationImage ??
                        '/assets/icons/Broccoli.svg'
                      }
                      alt="product"
                      className="h-[112px] w-[112px] rounded-full object-cover"
                    />
                    <h1 className="px-4 font-bold line-clamp-2">
                      {product?.name ?? 'Product Name'}
                    </h1>
                  </div>
                  <Button
                    title={isSelected ? 'Remove from list' : 'Add to list'}
                    onClick={() => handleAddRemove(product)}
                    className={`px-8 font-medium ${
                      isSelected ? 'bg-[#FFFFFF] text-[#202020]' : ''
                    }`}
                    shadow={false}
                  />
                </div>
              )}
              )}
            </div>
          ) : !loading ? (
            <>
              <div className="h-[362px] w-full flex-1 overflow-y-auto py-40 text-center text-lg font-medium italic text-[#6B6B6B]">
                No result found!
              </div>

              <div className="flex min-h-[255px] flex-col items-center justify-center gap-8 rounded-lg bg-[#F7F7F7] p-6 text-center">
                <div className="flex flex-col items-center gap-6">
                  <img
                    src="/images/pencil.svg"
                    alt="mail"
                    className="h-10 w-10"
                  />
                  <h1 className="text-lg font-semibold text-[#202020]">
                    Add product by manually
                  </h1>
                  <p className="text-[#202020]">
                    You can add product manually which you are offering but not
                    listed in Culineer.
                  </p>
                </div>
                <AddManually type="product" />
              </div>
            </>
          ) : (
            <div className="flex h-[90vh] items-center justify-center">
              <Spinner aria-label="loading" size="xl" color={'success'} />
            </div>
          )}
        </div>
        <div className="hidden flex-col gap-4 rounded-lg bg-[#F7F7F7] p-4 md:flex md:self-start xl:col-span-5 sticky top-4 max-h-[calc(100vh-2rem)]">
          <div>
            <h3 className="text-lg font-semibold">Added Products</h3>
            <p>Add the items you are producing.</p>
          </div>
          <div className="flex flex-1 flex-col gap-3 overflow-y-auto">
            {formData?.ingredients?.map((product, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between rounded-lg border border-[#CCE9AD] bg-white p-2 shadow-sm"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <img
                    src={
                      product?.illustrationImage || '/assets/icons/Broccoli.svg'
                    }
                    alt="product"
                    className="h-[61px] w-[61px] rounded-full object-cover"
                  />
                  <h1 className="font-bold line-clamp-2">{product?.name ?? "Product Name"}</h1>
                </div>
                <button
                  type="button"
                  aria-label="Remove product"
                  onClick={() => handleAddRemove(product)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[5px] bg-[#F7F7F7] text-[#202020] hover:text-red-500"
                >
                  <X className="h-5 w-5" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
          <SubmitButton className="w-full text-center" type="submit" disable={loading || addProductLoading} onClick={handleSubmit}>
            {loading || addProductLoading ? (
              <FontAwesomeIcon
                icon={faSpinner}
                className={'text-center h-6 w-6 animate-spin text-white text-sm'}
              />
            ) : (
              'Submit'
            )}
          </SubmitButton>
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-10 bg-white shadow-lg pb-[env(safe-area-inset-bottom)] md:hidden">
        <div className="flex w-full justify-end p-4">
          <SubmitButton className="text-center w-full" type="submit" disable={loading || addProductLoading} onClick={handleSubmit}>
            {loading || addProductLoading ? (
              <FontAwesomeIcon
                icon={faSpinner}
                className={'text-center h-6 w-6 animate-spin text-white text-sm'}
              />
            ) : (
              'Submit'
            )}
          </SubmitButton>
        </div>
      </div>
    </>
  )
}
